import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import Lenis from "lenis";

const LenisContext = createContext<Lenis | null>(null);

export function useLenis() {
  return useContext(LenisContext);
}

const NAVBAR_OFFSET = 100;

/**
 * Smooth scroll có quán tính (Lenis): sau khi thả bánh xe, vị trí còn “trôi” nhẹ rồi mới dừng hẳn —
 * trong thiết kế web thường gọi là smooth scroll / inertial scrolling / “scroll mượt có quán tính”.
 */
export function SmoothScroll({ children }: { children: ReactNode }) {
  const [lenis, setLenis] = useState<Lenis | null>(null);
  const [nativeTouchFlow, setNativeTouchFlow] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 1366px), (hover: none) and (pointer: coarse)").matches,
  );
  const location = useLocation();

  useEffect(() => {
    const previousScrollRestoration = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";

    return () => {
      window.history.scrollRestoration = previousScrollRestoration;
    };
  }, []);

  useEffect(() => {
    const media = window.matchMedia("(max-width: 1366px), (hover: none) and (pointer: coarse)");
    const update = () => setNativeTouchFlow(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    if (nativeTouchFlow) {
      setLenis(null);
      return;
    }
    const instance = new Lenis({
      smoothWheel: true,
      /** Càng nhỏ càng “trôi” lâu sau khi ngừng lăn (mặc định Lenis ~0.075–0.1) */
      lerp: 0.065,
      wheelMultiplier: 0.95,
      touchMultiplier: 1,
      prevent: (node) => node.closest?.("[data-native-interaction]") !== null,
      autoRaf: true,
      stopInertiaOnNavigate: true,
    });
    setLenis(instance);
    return () => {
      instance.destroy();
      setLenis(null);
    };
  }, [nativeTouchFlow]);

  useEffect(() => {
    lenis?.start();

    const { hash } = location;
    if (!hash) {
      if (lenis) {
        lenis.scrollTo(0, { immediate: true, force: true });
      } else {
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      }
      return;
    }

    const frame = requestAnimationFrame(() => {
      const element = document.getElementById(decodeURIComponent(hash.slice(1)));
      if (!element) return;

      if (lenis) {
        lenis.scrollTo(element, { offset: -NAVBAR_OFFSET });
      } else {
        const top = element.getBoundingClientRect().top + window.scrollY - NAVBAR_OFFSET;
        window.scrollTo({ top, left: 0, behavior: "auto" });
      }
    });

    return () => cancelAnimationFrame(frame);
  }, [lenis, location.key, location.pathname, location.hash]);

  return <LenisContext.Provider value={lenis}>{children}</LenisContext.Provider>;
}
